import { Component, OnInit } from "@angular/core";
import { Store, Action } from "@ngrx/store";
import { Actions } from "@ngrx/effects";
import { AppState } from "@store/app-state";
import { ConstantsStore } from "@store/constants";
import { ContactsStore } from "@store/contacts";
import { EnrolmentsStore } from "@store/enrolment";
import { Observable } from "rxjs/Observable";
import { Contact } from "@models/contact";
import { Enrolment } from "@models/enrolment";
import { Course } from "@models/course";
import { Campus } from "@models/campus";
import { Day } from "@models/day";
import { Term } from "@models/term";
import { MatrixClass } from "@models/matrix-class";
import { CompleterService, CompleterData, CompleterItem } from "ng2-completer";
import { ModalService, ModalConfig } from "@services/modal";
import { EnrolmentFormComponent } from "./enrolment-form"
import { PERIODS_BY_CAMPUS } from "@constants/periods";
import { NUMBER_OF_ENROLMENTS_PER_CLASS_PER_CAMPUS } from "@constants/matrix_classes";
import {
  FETCH_POTENTIAL_CLASSES,
  POPULATE_POTENTIAL_CLASSES,
  POPULATE_EDITABLE_ENROLMENT,
  POPULATE_ENROLMENTS,
  FETCH_ENROLMENT_SELECTED_CLASS,
  FETCH_ENROLMENT_SELECTED_CONTACT,
  POPULATE_SELECTED_CLASS,
  POPULATE_ENROLMENT_SELECTED_CONTACT
} from "@reducers/enrolments";
import { EnrolmentEffects } from "@reducers/effects/enrolment";

@Component({
  selector: 'enrolment-timetable-view',
  templateUrl: './enrolment-timetable-view.pug'
})
export class EnrolmentTimetableViewComponent implements OnInit {

  public contact: Contact
  public enrolment: Enrolment

  public campus: Campus
  public term: Term
  public course: Course
  public potentialClasses: MatrixClass[]
  public selectedClasses: MatrixClass[] = []

  public campuses: Campus[]
  public days: Day[]
  public holidayTermDays: Day[]
  public normalTermDays: Day[]
  public terms: Term[]
  public courses: Course[]
  public periodsByCampus: any = PERIODS_BY_CAMPUS
  public maxPerCampus: number = NUMBER_OF_ENROLMENTS_PER_CLASS_PER_CAMPUS

  public campusesSearchStr: string
  public termSearchStr: string
  public coursesStrSearch: string
  public campusesCompleter: CompleterData
  public termsCompleter: CompleterData
  public coursesCompleter: CompleterData

  public loadingPotentialClasses: boolean = false

  constructor(
    public store: Store<AppState>,
    public completerService: CompleterService,
    public enrolmentEffects: EnrolmentEffects,
    public modalService: ModalService,
    private actions: Actions,
  ) {}

  ngOnInit() {
    this.store.select("contactsReducer").subscribe((s: ContactsStore) => {
      this.contact    = s.activeContact })

    this.store.select("enrolmentsReducer").subscribe((s: EnrolmentsStore) => {
      this.enrolment          = s.editableEnrolment
      this.potentialClasses   = s.potentialClasses
    })

    this.store.select("constantsReducer").subscribe((s: ConstantsStore) => {
      this.campuses           = s.constants.campuses
      this.terms              = s.constants.terms
      this.days               = s.constants.days
      this.courses            = s.constants.courses

      this.setupDays()
      this.setupCompleters()
    })

    this.setInitialFormValues()
    this.setPotentialClasses()
  }

  getPeriodsForDayWithMostPeriods(): any[] {
    let result: any[] = []

    const campusPeriods: any = this.periodsByCampus[this.campus.code]
    Object.keys(campusPeriods).map((key: string) => {
      const periods: any[] = campusPeriods[key]
      if (periods.length > result.length) { result = periods }
    })

    return result
  }

  getClassesForDayAndPeriod(day: Day, period: number): MatrixClass[] {
    let result: MatrixClass[] = []

    this.potentialClasses.forEach((c: MatrixClass) => {
      if (c.period === period && c.dayId === day.id) { result.push(c) }
    })

    return result
  }

  getCssClassesForTimetableCell(day: Day, period: number) {
    return {
      'class-cell': true,
      'no-classes': this.getClassesForDayAndPeriod(day, period).length === 0,
      'has-available-enrolments': this.getClassesForDayAndPeriod(day, period).length > 0,
    }
  }

  getTimetableDisplayForDayAndPeriod(day: Day, period: number) {
    let result = ""

    const matrixClasses = this.getClassesForDayAndPeriod(day, period)

    if      (matrixClasses.length === 0) { result = "" }
    else if (matrixClasses.length === 1) { result = `${matrixClasses.length} Class` } 
    else    { result = `${matrixClasses.length} Classes` }

    return result
  }

  getDisplayNameForDayCode(code: string): string {
    const hay = [
      { code: "M", display: "Mon" },
      { code: "T", display: "Tue" },
      { code: "W", display: "Wed" },
      { code: "R", display: "Thu" },
      { code: "F", display: "Fri" },
      { code: "S", display: "Sat" },
      { code: "U", display: "Sun" },
      { code: "A", display: "Accel" }
    ]

    return hay.find((h: any) => h.code == code).display
  }

  getRelevantDays(): Day[] {
    let result: Day[] = []

    if (this.term.isHoliday) {
      result = this.holidayTermDays
    }
    else {
      result = this.normalTermDays
    }

    return result
  }

  setupDays() {
    this.holidayTermDays = this.days.filter((d: Day) => d.code == "A")
    this.normalTermDays = this.days.filter((d: Day) => d.code != "A")
  }

  setupCompleters() {
    this.campusesCompleter =
      this.completerService.local(this.campuses, "name", "name")
    this.termsCompleter =
      this.completerService.local(this.terms, "name", "name")
    this.coursesCompleter =
      this.completerService.local(this.courses, "name code humanised", "name")
  }

  selectedCampus(e: CompleterItem) {
    if (e) {
      this.campus = e.originalObject as Campus
      this.setPotentialClasses()
    }
  }

  selectedTerm(e: CompleterItem) {
    if (e) {
      this.term = e.originalObject as Term
      this.setPotentialClasses()
    }
  }

  selectedCourse(e: CompleterItem) {
    if (e) {
      this.course = e.originalObject as Course
      this.setPotentialClasses()
    }
  }

  setPotentialClasses() {
    const params: any = {}

    if (this.term) { params.termId = this.term.id }
    if (this.campus) { params.campusId = this.campus.id }
    if (this.course) { params.courseId = this.course.id }

    this.selectedClasses = []
    this.loadingPotentialClasses = true
    this.store.dispatch({ type: FETCH_POTENTIAL_CLASSES, payload: params })

    this.enrolmentEffects.fetchPotentialClasses.take(1).subscribe((a: Action) => {
      if (a.type === POPULATE_POTENTIAL_CLASSES) {
        this.loadingPotentialClasses = false
      }
    })
  }

  getMaxEnrolmentsForClass(matrixClass: MatrixClass) {
    return this.maxPerCampus[matrixClass.campus.code]
  }

  getStyleForEnrolmentBar(matrixClass: MatrixClass): any {
    let width = matrixClass.enrolments.length / this.getMaxEnrolmentsForClass(matrixClass) * 100 

    if (width > 100) {
      width = 100
    }

    return { width: `${width}%` }
  }

  setInitialFormValues() {
    this.campus = this.campuses[0]
    this.term = this.terms[0]
  }
  
  clickedClassCell(day: Day, period: number) {
    this.selectedClasses = this.getClassesForDayAndPeriod(day, period)
  }

  clickedSelectedClass(matrixClass: MatrixClass) {
    this.enrolment.matrixClassId  = matrixClass.id
    this.enrolment.cost           = matrixClass.course.cost

    this.store.dispatch({ type: POPULATE_EDITABLE_ENROLMENT, payload: this.enrolment })
    this.store.dispatch({ type: FETCH_ENROLMENT_SELECTED_CLASS, payload: this.enrolment.matrixClassId })
    this.store.dispatch({ type: FETCH_ENROLMENT_SELECTED_CONTACT, payload: this.enrolment.contactId })

    const one$ = this.actions.ofType(POPULATE_SELECTED_CLASS)
    const two$ = this.actions.ofType(POPULATE_SELECTED_CLASS)

    Observable.zip(one$, two$)
      .take(1)
      .subscribe(() => this.modalService.showModal(new ModalConfig(EnrolmentFormComponent)))
  }

}



// WEBPACK FOOTER //
// /Users/jason/src/matrix/client/src/app/components/enrolment-timetable-view.ts